import React, { useEffect, useRef, useState, useCallback } from "react";
import AddPartForm from "./AddPartsForm";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash } from "react-icons/fa";
import { API_BASE_URL } from "../../config/api";



const AdminParts = () => {
  const navigate = useNavigate();

  const dialogRef = useRef(null);
  const formRef = useRef(null);

  const [parts, setParts] = useState([]);
  const [filter, setFilter] = useState("All");
  const [categories, setCategories] = useState([]);
  const [editingPart, setEditingPart] = useState(null);
  const [mode, setMode] = useState("add");

  // ================= FETCH PARTS =================
  const fetchParts = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");

      const res = await fetch(`${API_BASE_URL}/api/parts`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.message || "Fetch error");

      const safeData = Array.isArray(data) ? data : [];
      setParts(safeData);

      // auto categories
      const uniqueCategories = [
        ...new Set(safeData.map((p) => p.category).filter(Boolean)),
      ];
      setCategories(uniqueCategories);
    } catch (err) {
      console.error("FETCH ERROR:", err);
    }
  }, []);

  // ================= INIT =================
  useEffect(() => {
    const storedUser = localStorage.getItem("user");

    if (!storedUser) {
      navigate("/login");
      return;
    }

    fetchParts();
  }, [fetchParts, navigate]);

  // ================= OPEN ADD =================
  const openAdd = () => {
    setMode("add");
    setEditingPart(null);
    formRef.current?.resetForm();
    dialogRef.current?.showModal();
  };

  // ================= OPEN EDIT =================
  const openEdit = (part) => {
    setMode("edit");
    setEditingPart(part);

    setTimeout(() => {
      dialogRef.current?.showModal();
    }, 0);
  };

  // ================= CLOSE =================
  const closeDialog = () => {
    dialogRef.current?.close();
    formRef.current?.resetForm();
    setEditingPart(null);
    setMode("add");
  };

  // ================= SAVE =================
  const handleSavePart = async (formData) => {
    try {
      const token = localStorage.getItem("authToken");

      const url =
        mode === "edit"
          ? `${API_BASE_URL}/api/parts/${editingPart.id}`
          : `${API_BASE_URL}/api/parts`;

      const method = mode === "edit" ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        console.error("SERVER ERROR:", data);
        alert(data.message || data.error || "Error saving part");
        return;
      }

      await fetchParts();
      closeDialog();
    } catch (err) {
      console.error("SAVE ERROR:", err);
      alert("Something went wrong");
    }
  };

  // ================= DELETE =================
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;

    try {
      const token = localStorage.getItem("authToken");

      const res = await fetch(`${API_BASE_URL}/api/parts/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("Delete failed");

      setParts((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error("DELETE ERROR:", err);
    }
  };

  // ================= FILTER =================
  const filteredParts =
    filter === "All"
      ? parts
      : parts.filter((p) => p.category === filter);

  return (
    <div className="flex h-screen bg-gray-100 text-gray-800">
      <main className="flex-1 p-6 ml-64 overflow-auto">

        {/* HEADER */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Pjeset </h1>

          <button
            onClick={openAdd}
            className="bg-blue-600 text-white px-4 py-2 rounded"
          >
            + Shto Pjese
          </button>
        </div>

        {/* FILTER */}
        <div className="mb-6">
          <select
            className="p-2 border rounded"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="All">All</option>

            {categories.map((cat, index) => (
              <option key={index} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        {/* TABLE */}
        <div className="bg-white rounded-lg shadow overflow-x-auto w-full min-w-[890px]">
          <table className="w-full text-sm">

            <thead className="bg-blue-600 text-white">
              <tr>
                <th className="p-3">ID</th>
                <th className="p-3">Foto</th>
                <th className="p-3">Emri</th>
                <th className="p-3">Kategori</th>
                <th className="p-3">Pajisja</th>
                <th className="p-3">Modeli</th>
                <th className="p-3">Brandi</th>
                <th className="p-3">Stock</th>
                <th className="p-3">Cmimi</th>
                <th className="p-3">Aksione</th>
              </tr>
            </thead>

            <tbody className="text-center">
              {filteredParts.map((p) => (
                <tr key={p.id} className="border-b">

                  <td className="p-3">{p.id}</td>

                  <td className="p-3">
                    {p.image ? (
                      <img
                        src={p.image}
                        className="w-16 h-12 object-cover rounded"
                      />
                    ) : (
                      <div className="w-16 h-12 bg-gray-200 rounded" />
                    )}
                  </td>

                  <td className="p-3 font-semibold">{p.name}</td>
                  <td className="p-3">{p.category}</td>
                  <td className="p-3">{p.device || "-"}</td>
                  <td className="p-3">{p.model || "-"}</td>
                  <td className="p-3">{p.brand || "-"}</td>
                  <td className="p-3">{p.stock ?? 0}</td>

                  <td className="p-3 text-green-600 font-bold">
                    €{Number(p.price || 0).toFixed(2)}
                  </td>

                  <td className="p-3 flex gap-2 justify-center">
                    <button onClick={() => openEdit(p)}>
                      <FaEdit />
                    </button>

                    <button onClick={() => handleDelete(p.id)}>
                      <FaTrash />
                    </button>
                  </td>

                </tr>
              ))}
            </tbody>

          </table>
        </div>

        {/* DIALOG */}
        <dialog ref={dialogRef} className="rounded-xl shadow-xl w-[600px]">
          <div className="p-4 border-b flex justify-end">
                   <button
                    onClick={closeDialog}
                    className="text-gray-500 hover:text-red-600 hover:bg-gray-100 w-8 h-8 rounded-full flex items-center justify-center transition"
                  >
                    ✕
                  </button>
                  </div>

          <div className="p-4">
            <AddPartForm
              ref={formRef}
              onAdd={handleSavePart}
              initialData={editingPart}
            />
          </div>
        </dialog>

      </main>
    </div>
  );
};

export default AdminParts;